import {
    computeStyles,
    DEFAULTS,
    PRESETS,
    type PresetName,
    RING_COUNT,
    type ThinkingIconParams,
} from 'components/thinking_icon/thinkingIconConfig';
import { forwardRef, useId } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { tokens } from 'styles/tokens';
import { createSeq } from 'utils/array';

const COLORS = {
    fg: '#88c0d0',
    bg: tokens.color.navy800,
};

const ARC_SEGMENTS = 79;
const ARC_STEP = Math.PI / ARC_SEGMENTS;

/**
 * utility for constructing svg paths along arcs of a unit circle.
 * Using segments to facilitate gradient in a piecewise way.
 */
function arcPath(from: number, to: number): string {
    const pts: string[] = [];
    for (let i = from; i <= to; i++) {
        const θ = i * ARC_STEP;
        pts.push(`${Math.sin(θ).toFixed(4)} ${Math.cos(θ).toFixed(4)}`);
    }
    return `M${pts[0]} ${pts
        .slice(1)
        .map((p) => `L${p}`)
        .join(' ')}`;
}

const FRONT_D = arcPath(0, ARC_SEGMENTS);
const BACK_D = arcPath(ARC_SEGMENTS + 1, 2 * ARC_SEGMENTS - 1);

const RING_INDICES = createSeq(RING_COUNT);

const breathe = keyframes`
    0%, 100% { transform: scaleX(var(--minS, 0.14)); }
    50% { transform: scaleX(var(--maxS, 0.79)); }
`;

const precess = keyframes`
    0%, 100% { transform: rotate(calc(var(--pa, 0deg) * -1)); }
    50% { transform: rotate(var(--pa, 0deg)); }
`;

const pausedStyles = css`
    .ring-anim {
        animation: none;
        transform: scaleX(calc((var(--minS) + var(--maxS)) / 2));
    }
    .arm-precess {
        animation: none;
        transform: none;
    }
`;

const Wrapper = styled.div<{ $size: string }>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: ${({ $size }) => $size};
    height: ${({ $size }) => $size};
    overflow: hidden;
    background-color: ${COLORS.bg};
    transition:
        width 400ms ease,
        height 400ms ease;
`;

const StyledSvg = styled.svg<{ $animated: boolean }>`
    .ring-anim {
        animation: ${breathe} var(--dur, 6s) ease-in-out infinite;
        animation-delay: var(--bd, 0s);
        transform-box: fill-box;
        transform-origin: center;
        will-change: transform;
    }

    .arm-precess {
        animation: ${precess} var(--pdur, 6s) ease-in-out infinite;
        animation-delay: var(--pd, 0s);
        transform-box: fill-box;
        transform-origin: center;
        will-change: transform;
    }

    @media (prefers-reduced-motion: reduce) {
        ${pausedStyles}
    }

    ${({ $animated }) => !$animated && pausedStyles}
`;

type ThinkingIconProps = Readonly<{
    preset?: PresetName;
    params?: ThinkingIconParams;
    animated?: boolean;
    size?: string;
    className?: string;
}>;

export const ThinkingIcon = forwardRef<HTMLDivElement, ThinkingIconProps>(
    function ThinkingIcon(
        { preset = 'idle-attentive', params, animated = true, size = '4rem', className },
        ref,
    ) {
        const resolved: ThinkingIconParams = params ?? PRESETS[preset];
        const dashed = resolved.dashed ?? DEFAULTS.dashed;
        const { svgVars, rings } = computeStyles(resolved);
        const frontGradId = `frontGrad-${useId()}`;
        const backGradId = `backGrad-${useId()}`;

        return (
            <Wrapper ref={ref} $size={size} className={className}>
                <StyledSvg
                    $animated={animated}
                    width="100%"
                    height="100%"
                    viewBox="-125 -125 250 250"
                    style={svgVars}
                >
                    <defs>
                        <linearGradient
                            id={frontGradId}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="0"
                            gradientUnits="objectBoundingBox"
                        >
                            <stop offset="0" stopColor={COLORS.fg} stopOpacity="0.5" />
                            <stop offset="1" stopColor={COLORS.fg} stopOpacity="1" />
                        </linearGradient>
                        <linearGradient
                            id={backGradId}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="0"
                            gradientUnits="objectBoundingBox"
                        >
                            <stop offset="0" stopColor={COLORS.fg} stopOpacity="0.16" />
                            <stop offset="1" stopColor={COLORS.fg} stopOpacity="0.5" />
                        </linearGradient>
                    </defs>
                    {RING_INDICES.map((i) => (
                        <g key={i} className="arm-precess" style={rings[i].precessVars}>
                            <g transform={rings[i].transform}>
                                <g className="ring-anim" style={rings[i].breatheVars}>
                                    <g
                                        fill="none"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                    >
                                        <path
                                            stroke={`url(#${backGradId})`}
                                            strokeDasharray={dashed ? '5 6' : undefined}
                                            vectorEffect="non-scaling-stroke"
                                            d={BACK_D}
                                        />
                                        <path
                                            stroke={`url(#${frontGradId})`}
                                            vectorEffect="non-scaling-stroke"
                                            d={FRONT_D}
                                        />
                                    </g>
                                </g>
                            </g>
                        </g>
                    ))}
                </StyledSvg>
            </Wrapper>
        );
    },
);
